import { MapPin, MessageCircle, Phone, Clock, ExternalLink, Navigation, Instagram } from "lucide-react";
import { SectionHeading, Reveal, WHATSAPP_URL, PHONE_TEL, MAPS_URL, WHATSAPP_DISPLAY, PHONE_DISPLAY } from "./shared";

export default function Location() {
  return (
    <section id="unidade" className="relative py-24 sm:py-32 px-4 sm:px-8">
      <div className="max-w-7xl mx-auto">
        <SectionHeading
          overline="/// Loja & Contato"
          title="NO BAIRRO RIO BRANCO, EM SÃO LEOPOLDO"
          subtitle="Na Av. São Borja, com fácil acesso e estacionamento próximo. Atendemos toda a região metropolitana de Porto Alegre."
        />

        <div className="mt-12 grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            <Reveal>
              <div
                data-testid="location-address-card"
                className="h-full rounded-2xl border border-white/10 bg-[#2E373E] p-6 hover:border-[#99BA4B]/50 transition-colors duration-300"
              >
                <MapPin size={22} className="text-[#99BA4B]" />
                <h3 className="mt-4 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                  Endereço
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-100">
                  Av. São Borja, 412
                  <br />
                  Bairro Rio Branco, São Leopoldo — RS
                </p>
                <p className="mt-3 flex items-center gap-1.5 font-mono-tech text-[11px] text-zinc-400">
                  <Navigation size={12} className="text-[#ADCF5D]" />
                  Fácil acesso pela BR-116 e centro de São Leopoldo
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.08}>
              <div
                data-testid="location-hours-card"
                className="h-full rounded-2xl border border-white/10 bg-[#2E373E] p-6 hover:border-[#99BA4B]/50 transition-colors duration-300"
              >
                <Clock size={22} className="text-[#99BA4B]" />
                <h3 className="mt-4 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                  Horário de Funcionamento
                </h3>
                <ul className="mt-2 space-y-1.5 text-sm text-zinc-100">
                  <li>Seg a Sex: 09:00 — 12:00 e 13:00 — 18:30</li>
                  <li className="text-zinc-300">Sábado: fechado</li>
                  <li className="text-zinc-300">Domingo: fechado</li>
                </ul>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <a
                data-testid="location-whatsapp-link"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="h-full rounded-2xl border border-[#99BA4B]/40 bg-[#99BA4B]/10 p-6 block hover:bg-[#99BA4B]/20 hover:border-[#99BA4B]/70 transition-all duration-300 active:scale-[0.98]"
              >
                <MessageCircle size={22} className="text-[#ADCF5D]" />
                <h3 className="mt-4 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                  WhatsApp Direto
                </h3>
                <p className="mt-2 font-display text-xl text-white">{WHATSAPP_DISPLAY}</p>
                <p className="mt-1 text-xs text-zinc-400">Resposta média em 5 minutos</p>
              </a>
            </Reveal>

            <Reveal delay={0.16}>
              <a
                data-testid="location-phone-link"
                href={PHONE_TEL}
                className="h-full rounded-2xl border border-white/10 bg-[#2E373E] p-6 block hover:border-[#99BA4B]/60 transition-all duration-300 active:scale-[0.98]"
              >
                <Phone size={22} className="text-[#99BA4B]" />
                <h3 className="mt-4 font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                  Ligue para a Loja
                </h3>
                <p className="mt-2 font-display text-xl text-white">{PHONE_DISPLAY}</p>
                <p className="mt-1 text-xs text-zinc-400">Toque para ligar agora</p>
              </a>
            </Reveal>
            <Reveal delay={0.2} className="sm:col-span-2">
              <a
                data-testid="location-instagram-link"
                href="https://www.instagram.com/pressicelulares/"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-2xl border border-white/10 bg-[#2E373E] p-6 flex items-center gap-4 hover:border-[#99BA4B]/60 transition-all duration-300 active:scale-[0.99]"
              >
                <Instagram size={22} className="text-[#99BA4B]" />
                <div>
                  <h3 className="font-mono-tech text-[11px] tracking-[0.2em] uppercase text-zinc-400">
                    Instagram
                  </h3>
                  <p className="mt-1 font-display text-lg text-white">@pressicelulares</p>
                </div>
                <span className="ml-auto font-mono-tech text-[11px] tracking-wider uppercase text-[#ADCF5D]">
                  Seguir →
                </span>
              </a>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="relative h-full min-h-[340px] rounded-2xl border border-white/10 bg-[#2E373E] overflow-hidden">
              <div className="absolute inset-0 bg-grid-millimetric opacity-70" />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="absolute -inset-16 rounded-full border border-[#99BA4B]/20" />
                  <div className="absolute -inset-10 rounded-full border border-[#99BA4B]/30" />
                  <div className="absolute -inset-16 overflow-hidden rounded-full">
                    <div
                      className="animate-radar absolute inset-0"
                      style={{
                        background:
                          "conic-gradient(from 0deg, rgba(153,186,75,0.4), transparent 70deg)",
                      }}
                    />
                  </div>
                  <div className="relative w-5 h-5 rounded-full bg-[#99BA4B] shadow-[0_0_30px_rgba(153,186,75,0.8)]" />
                </div>
              </div>
              <div className="absolute top-5 left-5 font-mono-tech text-[10px] tracking-[0.25em] text-zinc-400 uppercase">
                Localização // Av. São Borja
              </div>
              <div className="absolute top-5 right-5 font-mono-tech text-[10px] text-[#ADCF5D]">
                29.7603° S, 51.1472° W
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <a
                  data-testid="location-map-btn"
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full bg-white/5 hover:bg-[#99BA4B] border border-white/15 hover:border-[#99BA4B] text-white hover:text-[#1D2328] font-bold text-sm px-5 py-3.5 rounded-xl backdrop-blur-md transition-all duration-200 active:scale-95"
                >
                  <ExternalLink size={16} />
                  Abrir no Google Maps
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
